import {Injectable} from '@angular/core';
import {QuotationService} from '../quotation-service/quotation.service';
import {ErrorMessages} from '../../model/ErrorMessages';
import {LoggedInUser} from '../../model/LoggedInUser';
import {VehicleUsage} from '../../model/VehicleUsage';
import {ParkingSecurity} from '../../model/ParkingSecurity';
import {isNullOrUndefined} from 'util';
import {VehicleDetailsViewComponentItem} from "../../api/models/vehicle-details-view-component-item";
import {Validators} from "@angular/forms";
import {PlanHolderDetailsViewComponentItem} from "../../api/models/plan-holder-details-view-component-item";
import {FieldId} from "../../model/activation/FieldId";
import {CoverDetailsViewComponentItem} from "../../api/models/cover-details-view-component-item";
import {BankDetailsViewComponentItem} from "../../api/models/bank-details-view-component-item";

@Injectable()
export class ValidationService {
  // Error Messages
  private _errorMessages: string[] = [];
// Regex
  private _hasNumber = /\d/;
  private _isnum = /^\d+$/;
  private lmsInvalidChars = '&';
  private emailTest = /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/;
  private cellNumberTest = /^((?:\+27|27)|0)([^0123459]{1})-?([0-9]{1})-?(\d{3})-?(\d{4})$/;
  private _loggedIn = false;
  private _addressCheckValue = null;
  private _vehicleSelectValue = null;
  private _nameTest = /(^[a-zA-Z]+([-\s'][a-zA-Z]+)*$)/;
  private _nameCharacters = 120;
  private _numericOnly = /(^[0-9]+$)/;
  private _alphanumericOnly = /(^[a-zA-Z0-9 ]+$)/;
  private _idNumber = /(((\d{2}((0[13578]|1[02])(0[1-9]|[12]\d|3[01])|(0[13456789]|1[012])(0[1-9]|[12]\d|30)|02(0[1-9]|1\d|2[0-8])))|([02468][048]|[13579][26])0229))(( |-)(\d{4})( |-)(\d{3})|(\d{7}))/;
  private _initialsTest = /(^[a-zA-Z]+([.\s][a-zA-Z]+)*$)/;


  constructor(private quotationService: QuotationService) {
  }

  get vehicleSelectValue(): any {
    return this._vehicleSelectValue;
  }

  set vehicleSelectValue(value: any) {
    this._vehicleSelectValue = value;
  }

  get addressCheckValue() {
    return this._addressCheckValue;
  }

  set addressCheckValue(value) {
    this._addressCheckValue = value;
  }

  get loggedIn(): boolean {
    return this._loggedIn;
  }

  set loggedIn(value: boolean) {
    this._loggedIn = value;
  }

  get errorMessages(): string[] {
    return this._errorMessages;
  }

  set errorMessages(value: string[]) {
    this._errorMessages = value;
  }

  get hasNumber(): RegExp {
    return this._hasNumber;
  }

  set hasNumber(value: RegExp) {
    this._hasNumber = value;
  }

  get isnum(): RegExp {
    return this._isnum;
  }

  set isnum(value: RegExp) {
    this._isnum = value;
  }

  public isLeadFormValid() {
    this.isNameValid();
    this.isSurnameValid();
    this.isCellPhoneValid();
    this.isIDValid();
    return (this.isNameValid() && this.isSurnameValid() && this.isCellPhoneValid() && this.isIDValid());
  }

  public retrievedLoggedInUser() {
    this.quotationService.getLoggedInUser().subscribe((loggedInUser: LoggedInUser) => {
      if (loggedInUser) {
        this.loggedIn = true;
        this.quotationService.quotationRequest.name = loggedInUser.name;
        this.quotationService.quotationRequest.surname = loggedInUser.surname;
        this.quotationService.quotationRequest.legalRefType = loggedInUser.idType;
        this.quotationService.quotationRequest.idNumber = loggedInUser.idType === 'IDN' ? loggedInUser.idNumber : null;
        this.quotationService.quotationRequest.passportNumber = loggedInUser.idType === 'PS' ? loggedInUser.idNumber : null;
        this.quotationService.quotationRequest.cellphone = loggedInUser.cellNumber;
      }
    });
  }

  public isNameValid() {
    if (this.quotationService.quotationRequest.name == null || this.quotationService.quotationRequest.name === '') {
      this.addErrorMessage(ErrorMessages.NAME);
      return false;
    }

    if (this.quotationService.quotationRequest.name.indexOf(this.lmsInvalidChars) !== -1) {
      this.addErrorMessage(ErrorMessages.NAME_TEXT_ONLY);
      return false;
    }

    if (this.quotationService.quotationRequest.name.length > this._nameCharacters) {
      this.addErrorMessage(ErrorMessages.NAME_LONG);
      return false;
    }

    if(!this._nameTest.test(this.quotationService.quotationRequest.name)) {
      this.addErrorMessage(ErrorMessages.NAME_TEXT_ONLY);
      return false;
    }
    return true;
  }

  public isSurnameValid() {
    if (this.quotationService.quotationRequest.surname == null || this.quotationService.quotationRequest.surname === '') {
      this.addErrorMessage(ErrorMessages.SURNAME);
      return false;
    }

    if (this.quotationService.quotationRequest.surname.length > this._nameCharacters) {
      this.addErrorMessage(ErrorMessages.SURNAME_LONG);
      return false;
    }

    if (this.quotationService.quotationRequest.surname.indexOf(this.lmsInvalidChars) !== -1) {
      this.addErrorMessage(ErrorMessages.SURNAME_TEXT_ONLY);
      return false;
    }

    if(!this._nameTest.test(this.quotationService.quotationRequest.surname)) {
      this.addErrorMessage(ErrorMessages.SURNAME_TEXT_ONLY);
      return false;
    }

    return true;
  }

  public isCellPhoneValid() {
    if (this.quotationService.quotationRequest.cellphone == null || this.quotationService.quotationRequest.cellphone === '') {
      this.addErrorMessage(ErrorMessages.CELL);
      return false;
    }

    if (this.quotationService.quotationRequest.cellphone.length < 10) {
      this.addErrorMessage(ErrorMessages.CELL_SHORT);
      return false;
    }
    if (this.quotationService.quotationRequest.cellphone.length > 10) {
      this.addErrorMessage(ErrorMessages.CELL_LONG);
      return false;
    }

    if (!this.cellNumberTest.test(this.quotationService.quotationRequest.cellphone)) {
      this.addErrorMessage(ErrorMessages.CELL_INVALID);
      return false;
    }
    return true;
  }

  public isEmailValid(): boolean {
    if (this.quotationService.quotationRequest.email == null || this.quotationService.quotationRequest.email === '') {
      this.addErrorMessage(ErrorMessages.EMAIL);
      return false;
    }

    if (!this.emailTest.test(this.quotationService.quotationRequest.email)) {
      this.addErrorMessage(ErrorMessages.EMAIL_INVALID);
      return false;
    }
    return true;
  }

  public isIDValid(): boolean {
    if (this.quotationService.quotationRequest.idNumber == null || this.quotationService.quotationRequest.idNumber === '') {
      this.addErrorMessage(ErrorMessages.ID);
      return false;
    }
    if (this.quotationService.quotationRequest.idNumber.length < 13) {
      this.addErrorMessage(ErrorMessages.ID_SHORT);
      return false;
    }
    if (this.quotationService.quotationRequest.idNumber.length > 13) {
      this.addErrorMessage(ErrorMessages.ID_LONG);
      return false;
    }
    if (!this.checkID(this.quotationService.quotationRequest.idNumber)) {
      this.addErrorMessage(ErrorMessages.ID_INVALID);
      return false;
    }
    return true;
  }

  public isPassportValid(): boolean {
    if (this.quotationService.quotationRequest.passportNumber == null || this.quotationService.quotationRequest.passportNumber === '') {
      this.addErrorMessage(ErrorMessages.PASSPORT_INVALID);
      return false;
    }
    if (this.quotationService.quotationRequest.passportNumber.length < 3) {
      this.addErrorMessage(ErrorMessages.PASSPORT_INVALID);
      return false;
    }
    return true;
  }

  public isPrimaryDriverNameValid() {
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.firstName == null || this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.firstName === '') {
      this.addErrorMessage(ErrorMessages.NAME);
      return false;
    }

    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.firstName.indexOf(this.lmsInvalidChars) !== -1) {
      this.addErrorMessage(ErrorMessages.NAME_TEXT_ONLY);
      return false;
    }

    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.firstName.length > this._nameCharacters) {
      this.addErrorMessage(ErrorMessages.NAME_LONG);
      return false;
    }

    if(!this._nameTest.test(this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.firstName)) {
      this.addErrorMessage(ErrorMessages.NAME_TEXT_ONLY);
      return false;
    }
    return true;
  }

  public isPrimaryDriverSurnameValid() {
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.surname == null || this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.surname === '') {
      this.addErrorMessage(ErrorMessages.SURNAME);
      return false;
    }

    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.surname.length > this._nameCharacters) {
      this.addErrorMessage(ErrorMessages.SURNAME_LONG);
      return false;
    }

    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.surname.indexOf(this.lmsInvalidChars) !== -1) {
      this.addErrorMessage(ErrorMessages.SURNAME_TEXT_ONLY);
      return false;
    }

    if(!this._nameTest.test(this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.surname)) {
      this.addErrorMessage(ErrorMessages.SURNAME_TEXT_ONLY);
      return false;
    }

    return true;
  }

  public isPrimaryDriverCellPhoneValid() {
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.cellphoneNumber == null || this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.cellphoneNumber === '') {
      this.addErrorMessage(ErrorMessages.CELL);
      return false;
    }

    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.cellphoneNumber.length < 10) {
      this.addErrorMessage(ErrorMessages.CELL_SHORT);
      return false;
    }
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.cellphoneNumber.length > 10) {
      this.addErrorMessage(ErrorMessages.CELL_LONG);
      return false;
    }

    if (!this.cellNumberTest.test(this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.cellphoneNumber)) {
      this.addErrorMessage(ErrorMessages.CELL_INVALID);
      return false;
    }
    return true;
  }

  public isPrimaryDriverEmailValid() {
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.emailAddress == null || this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.emailAddress === '') {
      this.addErrorMessage(ErrorMessages.EMAIL);
      return false;
    }

    if (!this.emailTest.test(this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.emailAddress)) {
      this.addErrorMessage(ErrorMessages.EMAIL_INVALID);
      return false;
    }
    return true;
  }

  public isPrimaryDriverIDValid() {
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue == null || this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue === '') {
      this.addErrorMessage(ErrorMessages.ID);
      return false;
    }
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue.length < 13) {
      this.addErrorMessage(ErrorMessages.ID_SHORT);
      return false;
    }
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue.length > 13) {
      this.addErrorMessage(ErrorMessages.ID_LONG);
      return false;
    }
    if (!this.checkID(this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue)) {
      this.addErrorMessage(ErrorMessages.ID_INVALID);
      return false;
    }
    return true;
  }

  public isPrimaryDriverPassportValid() {
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue == null || this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue === '') {
      this.addErrorMessage(ErrorMessages.PASSPORT_INVALID);
      return false;
    }
    if (this.quotationService.getQuoteVehicleAndDriverDetails.primaryDriver.legalRefValue.length < 3) {
      this.addErrorMessage(ErrorMessages.PASSPORT_INVALID);
      return false;
    }
    return true;
  }

  public isParkingSecurityValid() {
    if (this.quotationService.quotationRequest.parkingSecurity == null ||
      this.quotationService.quotationRequest.parkingSecurity === ParkingSecurity.SELECT_SECURITY) {
      this.addErrorMessage(ErrorMessages.PARKING_SECURITY);
      return false;
    }
    return true;
  }

  public isVehicleUsageValid() {
    if (this.quotationService.quotationRequest.vehicleUsage == null ||
      this.quotationService.quotationRequest.vehicleUsage === VehicleUsage.SELECT_USAGE) {
      this.addErrorMessage(ErrorMessages.VEHICLE_USAGE);
      return false;
    }
    return true;
  }

  public isParkingAddressValid() {
    if ((this.quotationService.quotationRequest.parkingAddress.addressDescr !== this.addressCheckValue) ||
    (this.quotationService.quotationRequest.parkingAddress.suburbCd === null ||
      this.quotationService.quotationRequest.parkingAddress.suburbCd === '' ||
      this.quotationService.quotationRequest.parkingAddress.suburbCd.length <= 4 ||
      this.quotationService.quotationRequest.parkingAddress.addressDescr === null ||
      this.quotationService.quotationRequest.parkingAddress.addressDescr === '')) {
      this.addErrorMessage(ErrorMessages.NIGHT_PARKING);
      return false;
    }
    return true;
  }

  public isNullEmptyOrUndefined(input: any): boolean {
    return isNullOrUndefined(input) || input === '';
  }

  public checkID(id: string) {
    const length = 13;
    const idNumber = id.trim();
    const c: string[] = idNumber.split('');
    const cint: number[] = [];
    for (let i = 0; i < length; i++) {
      if (i % 2 === 1) {
        cint[i] = parseInt(c[i], 10) * 2;
        if (cint[i] > 9) {
          cint[i] = 1 + cint[i] % 10;
        }
      } else {
        cint[i] = parseInt(c[i], 10);
      }
    }
    let sum = 0;
    for (let i = 0; i < length; i++) {
      sum += cint[i];
    }
    return sum !== 0 && sum % 10 === 0;
  }

  public isStepOneValid(): boolean {
    return this.isClientDetailsValid() && this.isVehicleDetailsValid();
    // return true;
  }

  public clearErrors() {
    this.errorMessages = [];
  }

  public isPlateOrVINValid(): boolean {
    if ((this.quotationService.quotationRequest.vehicleDetails.registrationNumber == null ||
      this.quotationService.quotationRequest.vehicleDetails.registrationNumber === '') &&
      (this.quotationService.quotationRequest.vehicleDetails.vinNumber == null ||
        this.quotationService.quotationRequest.vehicleDetails.vinNumber === '')) {

      if (!this.errorMessages.some(s => s.includes(ErrorMessages.PLATE_OR_VIN))) {
        this.addErrorMessage(ErrorMessages.PLATE_OR_VIN);
      }
      return false;
    }
    return true;
  }

  // Step Two Validation
  public isStepTwoValid(): boolean {
    // Force evaluation for error messages
    this.isParkingAddressValid();
    this.isParkingSecurityValid();
    this.isVehicleUsageValid();

    return this.isParkingAddressValid() && this.isParkingSecurityValid() && this.isVehicleUsageValid();
  }

  public clearSpaceAndCapitilize(param: string): void {
    this.quotationService.quotationRequest.vehicleDetails[param] =
      this.quotationService.quotationRequest.vehicleDetails[param].replace(' ', '').toUpperCase();
  }

  public containsErrorMessage(error: ErrorMessages) {
    if (this.errorMessages == null) {
      return false;
    }
    return this.errorMessages.some(s => s.includes(error));
  }

  isStepTwoAltValid() {
    return (this.isVehicleDetailsManualValid() && this.isLicenseDetailsValid() && this.isNumberOfClaimsValid());
  }

  isVehicleDetailsManualValid() {
    return (this.isVehicleDescriptionValid() && this.isColourValid());
  }

  isLicenseDetailsValid() {
    if (this.quotationService.licenseFound) {
      return true;
    }
    return (this.isYearIssueValid() && this.isLicenseTypeValid());
  }

  isColourValid() {
    if ((isNullOrUndefined(this.quotationService.quotationRequest.vehicleDetails.colorCd) ||
      this.quotationService.quotationRequest.vehicleDetails.colorCd === 'Select Colour')) {
      this.addErrorMessage(ErrorMessages.VEHICLE_COLOUR);
      return false;
    }
    return true;
  }

  isLicenseTypeValid() {
    if ((isNullOrUndefined(this.quotationService.quotationRequest.licenseDetails.description) ||
      this.quotationService.quotationRequest.licenseDetails.description === 'Select License Type')) {
      this.addErrorMessage(ErrorMessages.LICENSE_TYPE);
      return false;
    }
    return true;
  }

  isYearIssueValid() {
    if ((isNullOrUndefined(this.quotationService.quotationRequest.licenseDetails.yearOfFirstIssue))) {
      this.addErrorMessage(ErrorMessages.YEAR_ISSUE);
      return false;
    } else if (this.quotationService.quotationRequest.licenseDetails.yearOfFirstIssue < 1900 ||
      this.quotationService.quotationRequest.licenseDetails.yearOfFirstIssue > 2100 ||
      this.quotationService.quotationRequest.licenseDetails.yearOfFirstIssue > (new Date()).getFullYear()) {
      this.addErrorMessage(ErrorMessages.YEAR_ISSUE_INVALID);
      return false;
    }
    return true;
  }

  isVehicleDescriptionValid() {
    if ((this.quotationService.quotationRequest.vehicleDetails.description !== this.vehicleSelectValue) ||
      (isNullOrUndefined(this.quotationService.quotationRequest.vehicleDetails.modelRangeYearCode) ||
      this.quotationService.quotationRequest.vehicleDetails.modelRangeYearCode === '') ||
      (isNullOrUndefined(this.quotationService.quotationRequest.vehicleDetails.description) ||
        this.quotationService.quotationRequest.vehicleDetails.description === '')
    ) {
      this.addErrorMessage(ErrorMessages.VEHICLE_DESCRIPTION);
      return false;
    }
    return true;
  }

  isNumberOfClaimsValid() {
    if (isNullOrUndefined(this.quotationService.quotationRequest.licenseDetails.numberOfClaims)) {
      this.addErrorMessage(ErrorMessages.NUMBER_OF_CLAIMS);
      return false;
    } else if (this.quotationService.quotationRequest.licenseDetails.numberOfClaims < 0 ||
      this.quotationService.quotationRequest.licenseDetails.numberOfClaims > 100) {
      this.addErrorMessage(ErrorMessages.NUMBER_OF_CLAIMS_INVALID);
      return false;
    }
    return true;
  }

  private isClientDetailsValid() {
    // Force evaluation for error messages
    this.isNameValid();
    this.isSurnameValid();
    this.isCellPhoneValid();
    this.isEmailValid();
    this.isIDValid();

    return (this.isNameValid() && this.isSurnameValid() && this.isCellPhoneValid() && this.isEmailValid() && this.isIDValid());
  }

  private isVehicleDetailsValid(): boolean {
    // Force evaluation for error messages
    this.isPlateOrVINValid();
    this.isVehicleUsageValid();
    this.isParkingSecurityValid();
    this.isParkingAddressValid();

    return (this.isPlateOrVINValid() && this.isVehicleUsageValid() && this.isParkingSecurityValid() && this.isParkingAddressValid());
  }

  private addErrorMessage(error: ErrorMessages) {
    if (!this.containsErrorMessage(error)) {
      this.errorMessages.push(error);
    }
  }

  getFieldValidators(field: PlanHolderDetailsViewComponentItem | VehicleDetailsViewComponentItem | CoverDetailsViewComponentItem | BankDetailsViewComponentItem, showConditionMet?: boolean) {
    let validators = [];
    if (!field.showCondition || showConditionMet) {
      validators.push(Validators.required);
      if (field.validation && field.validation.maximumLength) {
        validators.push(Validators.maxLength(field.validation.maximumLength));
      }
      if (field.validation && field.validation.maximumValue) {
        validators.push(Validators.max(field.validation.maximumValue));
      }
      if (field.validation && field.validation.minimumValue) {
        validators.push(Validators.min(field.validation.minimumValue));
      }
      if (field.id === FieldId.PLAN_HOLDER_CONTACT_EMAIL) {
        validators.push(Validators.email)
      }
      if (field.id === FieldId.VEHICLE_AND_OWNER_PARTICULARS_ENGINE_NUMBER || field.id === FieldId.VEHICLE_AND_OWNER_PARTICULARS_VIN
        || field.id === FieldId.ADDRESS_DETAILS_NIGHT_TIME_STREET_NAME || field.id === FieldId.ADDRESS_DETAILS_DAY_TIME_STREET_NAME
        || field.id === FieldId.REGISTERED_OWNER_PASSPORT_NUMBER) {
        validators.push(Validators.pattern(this._alphanumericOnly));
      }
      if (field.id === FieldId.ADDRESS_DETAILS_NIGHT_TIME_STREET_NUMBER || field.id === FieldId.ADDRESS_DETAILS_DAY_TIME_STREET_NUMBER
        || field.id === FieldId.ADDRESS_DETAILS_NIGHT_TIME_UNIT_COMPLEX_NUMBER || field.id === FieldId.ADDRESS_DETAILS_DAY_TIME_UNIT_COMPLEX_NUMBER
        || field.id === FieldId.BANKING_DETAILS_ACCOUNT_NUMBER || field.id === FieldId.BANKING_DETAILS_PREMIUM_DEDUCTION_DATE) {
        validators.push(Validators.pattern(this._numericOnly));
      }
      if (field.id === FieldId.REGISTERED_OWNER_NAME || field.id === FieldId.REGISTERED_OWNER_INITIALS || field.id === FieldId.REGISTERED_OWNER_SURNAME
      || field.id === FieldId.BANKING_DETAILS_ACCOUNT_HOLDER_NAME || field.id === FieldId.BANKING_DETAILS_ACCOUNT_HOLDER_SURNAME) {
        validators.push(Validators.pattern(this._nameTest));
      }
      if (field.id === FieldId.REGISTERED_OWNER_ID_NUMBER) {
        validators.push(Validators.pattern(this._idNumber));
      }
      if (field.id === FieldId.PLAN_HOLDER_INITIALS || field.id === FieldId.PRIMARY_DRIVER_INITIALS) {
        validators.push(Validators.pattern(this._initialsTest));
      }
      if (field.id === FieldId.PLAN_HOLDER_CONTACT_CELLPHONE_NUMBER || field.id === FieldId.PRIMARY_DRIVER_CONTACT_CELLPHONE_NUMBER) {
        validators.push(Validators.pattern(this.cellNumberTest));
      }
      if (field.id === FieldId.PLAN_HOLDER_CONTACT_EMAIL || field.id === FieldId.PRIMARY_DRIVER_CONTACT_EMAIL) {
        validators.push(Validators.pattern(this.emailTest));
      }
    }
    return validators;
  }
}
